import type { DebugEvent, DebugEventInput } from "./types";

const maximumEventCount = 100;
const maximumTextLength = 160;
const sensitiveValuePattern =
  /(?:bearer\s+|password|secret|token=|signature=|credential=|eyJ[a-zA-Z0-9_-]{12,}\.)/i;

let nextId = 1;
let events: readonly DebugEvent[] = [];
const listeners = new Set<() => void>();

function sanitizeText(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;

  const normalized = value.replace(/[\u0000-\u001f\u007f]/g, " ").trim();
  if (sensitiveValuePattern.test(normalized)) return "[redacted]";

  return normalized.slice(0, maximumTextLength);
}

function sanitizeRoute(value: string | undefined): string | undefined {
  return sanitizeText(value?.split(/[?#]/, 1)[0]);
}

export function sanitizeDebugEvent(input: DebugEventInput): DebugEventInput {
  return {
    feature: sanitizeText(input.feature) ?? "unknown",
    operation: sanitizeText(input.operation) ?? "unknown",
    status: input.status,
    route: sanitizeRoute(input.route),
    correlationId: sanitizeText(input.correlationId),
    errorCode: sanitizeText(input.errorCode),
    durationMs:
      input.durationMs === undefined
        ? undefined
        : Math.max(0, Math.round(input.durationMs)),
    locale: input.locale,
    connectionState: sanitizeText(input.connectionState),
    reconciliationState: sanitizeText(input.reconciliationState),
    storageOperationState: sanitizeText(input.storageOperationState),
  };
}

export function emitDebugEvent(input: DebugEventInput): void {
  if (process.env.NODE_ENV === "production") return;

  const event: DebugEvent = {
    ...sanitizeDebugEvent(input),
    id: nextId++,
    occurredAt: Date.now(),
  };

  events = [...events.slice(-(maximumEventCount - 1)), event];
  listeners.forEach((listener) => listener());
}

export function clearDebugEvents(): void {
  events = [];
  listeners.forEach((listener) => listener());
}

export function getDebugEvents(): readonly DebugEvent[] {
  return events;
}

export function subscribeToDebugEvents(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
